import React, { useEffect, useMemo, useState } from "react";
import { PrintButton } from "../components/PrintButton";
import { getChanges, createChange, updateChange, deleteChange } from "../lib/api";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  ClipboardList, ChevronRight, Plus, Trash2, X, ChevronDown, CheckCircle2,
} from "lucide-react";

const STATUS = {
  draft: { label: "Draft", cls: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20" },
  submitted: { label: "Submitted", cls: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
  approved: { label: "Approved", cls: "bg-teal-500/10 text-teal-400 border-teal-500/20" },
  scheduled: { label: "Scheduled", cls: "bg-purple-500/10 text-purple-400 border-purple-500/20" },
  implemented: { label: "Implemented", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  closed: { label: "Closed", cls: "bg-emerald-700/10 text-emerald-300 border-emerald-700/30" },
  rejected: { label: "Rejected", cls: "bg-red-500/10 text-red-400 border-red-500/20" },
};
const STATUSES = Object.keys(STATUS);
const RISK = {
  low: { label: "Low", cls: "text-emerald-400" },
  medium: { label: "Medium", cls: "text-amber-400" },
  high: { label: "High", cls: "text-red-400" },
};
const RISKS = Object.keys(RISK);
const BLANK = { title: "", description: "", category: "", risk: "low", status: "draft", requested_by: "", approver: "", scheduled_for: "", implemented_at: "", pir: "" };

export default function Changes() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [openId, setOpenId] = useState(null);

  const load = () => getChanges().then((d) => { setRows(d); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.title.trim()) { toast.error("Enter a title"); return; }
    try {
      const c = await createChange(form);
      setRows((p) => [c, ...p]);
      setAdding(false); setForm(BLANK);
      toast.success(`${c.ref} created`);
    } catch { toast.error("Create failed"); }
  };
  const patch = async (id, payload) => {
    try {
      const u = await updateChange(id, payload);
      setRows((p) => p.map((r) => (r.id === id ? u : r)));
    } catch { toast.error("Update failed"); }
  };
  const remove = async (id) => {
    try { await deleteChange(id); setRows((p) => p.filter((r) => r.id !== id)); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const stats = useMemo(() => ({
    open: rows.filter((r) => !["closed", "rejected"].includes(r.status)).length,
    awaiting: rows.filter((r) => r.status === "submitted").length,
    scheduled: rows.filter((r) => r.status === "scheduled").length,
    highRisk: rows.filter((r) => r.risk === "high" && !["closed", "rejected"].includes(r.status)).length,
  }), [rows]);

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center px-5 sm:px-8 bg-zinc-950/60 backdrop-blur lg:sticky lg:top-0 z-10">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2">
          <span className="text-zinc-600">~/</span> change <ChevronRight className="h-3 w-3" /> management
        </span>
      </header>

      <div className="p-5 sm:p-8 max-w-5xl">
        <div className="no-print flex justify-end mb-4"><PrintButton /></div>
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-blue-400 font-semibold">
          <ClipboardList className="h-4 w-4" /> Change Management (ITIL)
        </div>
        <div className="flex items-start justify-between gap-4 mt-2">
          <div>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-white tracking-tight">Change Log</h1>
            <p className="text-zinc-400 mt-3 max-w-2xl">
              Raise, approve, schedule and review changes across the voice &amp; network estate — with risk,
              approvals and a post-implementation review (PIR) on every record.
            </p>
          </div>
          {!adding && (
            <button onClick={() => setAdding(true)} data-testid="add-change-btn" className="shrink-0 flex items-center gap-1.5 text-[13px] px-3 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white font-medium">
              <Plus className="h-4 w-4" /> Raise change
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
          <Stat label="Open" value={stats.open} testId="chg-stat-open" />
          <Stat label="Awaiting approval" value={stats.awaiting} testId="chg-stat-awaiting" tone={stats.awaiting > 0 ? "amber" : "default"} />
          <Stat label="Scheduled" value={stats.scheduled} testId="chg-stat-scheduled" />
          <Stat label="High risk (open)" value={stats.highRisk} testId="chg-stat-highrisk" tone={stats.highRisk > 0 ? "red" : "default"} />
        </div>

        {adding && (
          <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-950/50 p-5" data-testid="add-change-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <Input data-testid="chg-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Change title" className="sm:col-span-2 h-9 bg-zinc-900 border-zinc-800 text-sm" />
              <Textarea data-testid="chg-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description / implementation plan" className="sm:col-span-2 bg-zinc-900 border-zinc-800 text-sm min-h-[70px]" />
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Category (e.g. Voice, Network, Webex)" className="h-9 bg-zinc-900 border-zinc-800 text-sm" />
              <Select value={form.risk} onValueChange={(v) => setForm({ ...form, risk: v })}>
                <SelectTrigger data-testid="chg-risk" className="h-9 bg-zinc-900 border-zinc-800 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">{RISKS.map((r) => <SelectItem key={r} value={r}>{RISK[r].label} risk</SelectItem>)}</SelectContent>
              </Select>
              <Input value={form.requested_by} onChange={(e) => setForm({ ...form, requested_by: e.target.value })} placeholder="Requested by" className="h-9 bg-zinc-900 border-zinc-800 text-sm" />
              <Input value={form.approver} onChange={(e) => setForm({ ...form, approver: e.target.value })} placeholder="Approver (CAB)" className="h-9 bg-zinc-900 border-zinc-800 text-sm" />
              <Input value={form.scheduled_for} onChange={(e) => setForm({ ...form, scheduled_for: e.target.value })} placeholder="Scheduled window (e.g. 2026-08-15 22:00)" className="sm:col-span-2 h-9 bg-zinc-900 border-zinc-800 text-sm" />
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <button onClick={() => { setAdding(false); setForm(BLANK); }} className="px-3 py-1.5 rounded-md bg-zinc-800 hover:bg-zinc-700 text-sm">Cancel</button>
              <button onClick={add} data-testid="confirm-add-change" className="px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium">Create</button>
            </div>
          </div>
        )}

        <div className="mt-6 space-y-2">
          {loading ? (
            <div className="p-6 text-zinc-500 font-mono text-sm">Loading changes…</div>
          ) : rows.length === 0 ? (
            <div className="rounded-lg border border-dashed border-zinc-800 p-8 text-center text-zinc-600 text-sm" data-testid="changes-empty">No changes raised yet. Click "Raise change" to create the first record.</div>
          ) : (
            <div data-testid="changes-list" className="space-y-2">
              {rows.map((c) => {
                const open = openId === c.id;
                return (
                  <div key={c.id} data-testid={`change-row-${c.id}`} className="rounded-lg border border-zinc-800 bg-zinc-900 overflow-hidden">
                    <div className="flex items-center gap-3 px-4 py-3">
                      <button onClick={() => setOpenId(open ? null : c.id)} data-testid={`change-toggle-${c.id}`} className="flex-1 flex items-center gap-3 text-left min-w-0">
                        <ChevronDown className={`h-4 w-4 text-zinc-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                        <span className="font-mono text-[12px] text-zinc-500 shrink-0">{c.ref}</span>
                        <span className="text-sm text-white font-medium truncate">{c.title}</span>
                      </button>
                      <span className={`text-[11px] font-semibold shrink-0 ${RISK[c.risk]?.cls}`}>{RISK[c.risk]?.label}</span>
                      <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full border shrink-0 ${STATUS[c.status]?.cls}`}>{STATUS[c.status]?.label}</span>
                    </div>

                    {open && (
                      <div className="px-4 pb-4 pt-1 border-t border-zinc-800 space-y-3" data-testid={`change-detail-${c.id}`}>
                        {c.description && <p className="text-[13px] text-zinc-300 whitespace-pre-wrap mt-2">{c.description}</p>}
                        <div className="flex flex-wrap gap-x-5 gap-y-1 text-[12px] text-zinc-400">
                          {c.category && <span><span className="text-zinc-600">Category:</span> {c.category}</span>}
                          {c.requested_by && <span><span className="text-zinc-600">Requested by:</span> {c.requested_by}</span>}
                          {c.approver && <span><span className="text-zinc-600">Approver:</span> {c.approver}</span>}
                          {c.scheduled_for && <span><span className="text-zinc-600">Scheduled:</span> <span className="font-mono">{c.scheduled_for}</span></span>}
                        </div>

                        {/* Status workflow */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Status:</span>
                          <Select value={c.status} onValueChange={(v) => patch(c.id, { status: v })}>
                            <SelectTrigger data-testid={`change-status-${c.id}`} className="h-8 w-44 bg-zinc-950 border-zinc-800 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">{STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS[s].label}</SelectItem>)}</SelectContent>
                          </Select>
                          {c.status === "approved" && <span className="text-[12px] text-teal-400 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Approved by CAB</span>}
                        </div>

                        {/* PIR — shown once implemented/closed */}
                        {["implemented", "closed"].includes(c.status) && (
                          <div data-testid={`change-pir-${c.id}`}>
                            <label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold block mb-1.5">Post-Implementation Review (PIR)</label>
                            <Textarea
                              data-testid={`pir-input-${c.id}`}
                              defaultValue={c.pir}
                              onBlur={(e) => { if (e.target.value !== c.pir) patch(c.id, { pir: e.target.value }); }}
                              placeholder="Outcome, success criteria met?, issues, rollback used?, follow-ups…"
                              className="bg-zinc-950 border-zinc-800 text-sm min-h-[70px]"
                            />
                          </div>
                        )}

                        <div className="flex justify-end">
                          <button onClick={() => remove(c.id)} data-testid={`delete-change-${c.id}`} className="flex items-center gap-1.5 text-[12px] text-zinc-500 hover:text-red-400">
                            <Trash2 className="h-3.5 w-3.5" /> Delete record
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Always-printed full detail */}
                    <div className="print-show px-4 pb-4 pt-1 border-t border-zinc-800" data-testid={`change-print-${c.id}`}>
                      {c.description && <p className="text-[13px] text-zinc-300 whitespace-pre-wrap mt-2">{c.description}</p>}
                      <div className="text-[12px] text-zinc-400 mt-1">
                        Risk: {RISK[c.risk]?.label} · Status: {STATUS[c.status]?.label}
                        {c.category ? ` · Category: ${c.category}` : ""}
                        {c.requested_by ? ` · Requested by: ${c.requested_by}` : ""}
                        {c.approver ? ` · Approver: ${c.approver}` : ""}
                        {c.scheduled_for ? ` · Scheduled: ${c.scheduled_for}` : ""}
                      </div>
                      {c.pir && <p className="text-[12px] text-zinc-400 mt-1"><span className="text-zinc-600">PIR:</span> {c.pir}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, testId, tone = "default" }) => {
  const cls = tone === "red" ? "text-red-400" : tone === "amber" ? "text-amber-400" : "text-white";
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4" data-testid={testId}>
      <div className={`font-heading text-3xl font-extrabold ${cls}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mt-1">{label}</div>
    </div>
  );
};
